import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Button,
  MenuItem,
  IconButton,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { AgGridTable } from "../../Component/AgGridTable";
import { EnhancedPageTitle } from "../../Component/PageTitle";
import {
  fetchEmployeeMapping,
  fetchGetEmployees,
  fetchGetProjects,
  updateEmployeeMapping,
  deleteEmployeeMapping
} from "../../services/empMappingService";
import { FilterField, SearchDrawer } from "../../Component/SearchDrawer";
import CenteredCircularProgress from "../../Component/CenteredCircularProgress";
import BadgeIcon from "@mui/icons-material/Badge";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { EmployeeMappingData } from "../../Interface/EmpMapping";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const EmpMapping: React.FC = () => {
  const navigate = useNavigate();

  const [employeeMappingData, setEmployeeMappingData] = useState<EmployeeMappingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [projectList, setProjectList] = useState<any[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<EmployeeMappingData | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  // Load Data
  const loadData = async () => {
    try {
      setLoading(true);
      const res = await fetchEmployeeMapping();
      const activeMappings = Array.isArray(res)
        ? res.filter((item: any) => item.isDeleted === false || item.isDeleted === 0)
        : [];
      setEmployeeMappingData(activeMappings);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Concatenate First name & Last name
  const uniqueEmployeeNames = useMemo(() => {
    const seen = new Set();

    const fullNames = employeeList
      .map(emp => `${emp.firstName} ${emp.lastName}`)
      .filter(name => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      });
    return fullNames.sort((a, b) => a.localeCompare(b));
  }, [employeeList]);

  // Load Data
  useEffect(() => {
    loadData();
    const loadDropdowns = async () => {
      try {
        const empRes = await fetchGetEmployees();
        const projRes = await fetchGetProjects();
        setEmployeeList(empRes || []);
        setProjectList(projRes || []);
      } catch (error) {
        console.error("Dropdown load error:", error);
      }
    };
    loadDropdowns();
  }, []);

  // Handle Search
  const handleSearch = async (filters: Record<string, string>) => {
    try {
      setLoading(true);
      const res = await fetchEmployeeMapping(filters);
      const activeMappings = Array.isArray(res)
        ? res.filter((item: any) => item.isDeleted === false || item.isDeleted === 0)
        : [];
      setEmployeeMappingData(activeMappings);
      setDrawerOpen(false);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit
  const handleEditClick = (row: EmployeeMappingData) => {
    const emp = employeeList.find(e => `${e.firstName} ${e.lastName}` === row.employeeName);
    const proj = projectList.find(p => p.projectName === row.projectName);

    setSelectedRow({
      ...row,
      employeeId: emp?.employeeId ?? 0,
      projectId: proj?.projectId ?? 0,
    });

    setEditDialogOpen(true);
  };

  // Handle Delete
  const handleDeleteClick = (row: EmployeeMappingData) => {
    setSelectedRow(row);
    setDeleteDialogOpen(true);
  };

  // Confirm Edit
  const confirmEdit = async () => {
    if (!selectedRow) return;

    // Logic for StartDate & EndDate
    const { startDate, endDate, rate, utilization } = selectedRow;
    const start = new Date(startDate ?? "");
    const end = new Date(endDate ?? "");
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setSnackbarMessage("Please select valid Start and End Dates.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (start >= end) {
      setSnackbarMessage("Start Date must be before End Date.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (isNaN(Number(rate)) || isNaN(Number(utilization))) {
      setSnackbarMessage("Rate and Utilization must be valid numbers.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      employeeProjectMappingId: selectedRow.employeeProjectMappingId,
      employeeId: selectedRow.employeeId,
      projectId: selectedRow.projectId,
      rate: selectedRow.rate,
      utilization: selectedRow.utilization,
      type: selectedRow.type,
      startDate: selectedRow.startDate,
      endDate: selectedRow.endDate,
      currencyId: 1,
      isActive: true,
      isDeleted: false,
      createdBy: 0,
      createdOn: new Date().toISOString(),
      modifiedBy: 0,
      modifiedOn: new Date().toISOString(),
    };
    try {
      const res = await updateEmployeeMapping(payload);
      if (typeof res?.data === "string" && res.data.toLowerCase().includes("utilization")) {
        setSnackbarMessage(res.data);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      setEditDialogOpen(false);
      setSnackbarMessage("Update successful!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await loadData();
    } catch (error: any) {
      const backendMsg =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        "Something went wrong while updating.";
      setSnackbarMessage(backendMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Confirm Delete
  const confirmDelete = async () => {
    if (!selectedRow) return;
    const id = Number(selectedRow.employeeProjectMappingId);
    console.log("Sending DELETE for ID:", id);
    try {
      const res = await deleteEmployeeMapping(id);
      console.log("Delete API response:", res);
      const message = typeof res?.data === "string" ? res.data.toLowerCase() : "";
      if (message.includes("already exists")) {
        setSnackbarMessage("Data can't be deleted as it is linked with a timesheet.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      setDeleteDialogOpen(false);
      setSnackbarMessage("Mapping deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await loadData();
    } catch (error: any) {
      const backendMsg =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        "Something went wrong while deleting.";
      if (
        backendMsg.toLowerCase().includes("already exists") ||
        backendMsg.toLowerCase().includes("linked with timesheet")
      ) {
        setSnackbarMessage("Data can't be deleted as it is linked with a timesheet.");
      } else {
        setSnackbarMessage(backendMsg);
      }
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // Fields for Search Drawer
  const filterFields: FilterField[] = useMemo(() => [
    {
      key: "projectName",
      label: "Project",
      type: "dropdown",
      options: projectList.map(p => p.projectName),
    },
    {
      key: "employeeName",
      label: "Employee Name",
      type: "dropdown",
      options: uniqueEmployeeNames,
    },
  ], [employeeList, projectList]);

  return (
    <Box sx={{ padding: 3, backgroundColor: "rgb(238, 242, 246)" }}>
      <EnhancedPageTitle
        title="Project Mapping"
        icon={<BadgeIcon />}
        button1Text="Add New"
        button1Action={() => navigate("/AddMapping")}
        button2Text="Search"
        button2Action={() => setDrawerOpen(true)}
      />
      {loading ? <CenteredCircularProgress /> : (
        <>
          <SearchDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            filterFields={filterFields}
            onSearch={handleSearch}
            onReset={loadData}
          />

          <AgGridTable
            domLayout="autoHeight"
            customStyles="legacy"
            rowSelection="single"
            gridOptions={{}}
            columns={[
              { field: "empCode", headerName: "Emp Code", filter: true, minWidth: 100, maxWidth: 200 },
              { field: "employeeName", headerName: "Emp Name", tooltipField: "employeeName", filter: true },
              { field: "projectName", headerName: "Project", filter: true },
              {
                field: "rate",
                headerName: "Rate",
                cellStyle: { textAlign: "right" },
                valueFormatter: (params: any) => params.value != null ? `$${params.value}` : "",
                filter: true,
                minWidth: 100, maxWidth: 120,
              },
              {
                field: "utilization",
                headerName: "Utilization",
                valueFormatter: (params: any) => params.value != null ? `${params.value}%` : "",
                filter: true,
                minWidth: 100, maxWidth: 100, cellStyle: { textAlign: "right" },
              },
              { field: "startDate", headerName: "Start Date", filter: true, cellStyle: { textAlign: "center" }, minWidth: 100, maxWidth: 150 },
              { field: "endDate", headerName: "End Date", filter: true, cellStyle: { textAlign: "center" }, minWidth: 100, maxWidth: 150 },
              {
                field: "actions",
                headerName: "Actions",
                minWidth: 120,
                maxWidth: 120,
                cellRenderer: (params: any) => (
                  <>
                    <IconButton onClick={() => handleEditClick(params.data)}>
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(params.data)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </>
                ),
              },
            ]}
            data={employeeMappingData}
            handleSelectedRows={() => { }}
            noDataComponentText="No Employee data available"
            pagination={false}
            paginationPerPage={10}
          />

          {/* Edit Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Mapping</DialogTitle>
            <DialogContent>

              {/* Employee Name Dropdown */}
              <TextField
                select
                label="Employee Name"
                fullWidth
                margin="normal"
                value={selectedRow?.employeeName || ""}
                onChange={(e) => {
                  const name = e.target.value;
                  const emp = employeeList.find(emp => `${emp.firstName} ${emp.lastName}` === name);
                  if (emp) {
                    setSelectedRow((prev) =>
                      prev ? {
                        ...prev,
                        employeeName: name,
                        empCode: emp.employeeCode,
                        employeeId: emp.employeeId,
                      } : null
                    );
                  }
                }}
              >
                {uniqueEmployeeNames.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}

              </TextField>

              {/* Employee Code (readonly) */}
              <TextField
                label="Employee Code"
                fullWidth
                margin="normal"
                value={selectedRow?.empCode || ""}
                InputProps={{ readOnly: true }}
              />

              {/* Project Dropdown */}
              <TextField
                select
                label="Project"
                fullWidth
                margin="normal"
                value={selectedRow?.projectName || ""}
                onChange={(e) => {
                  const projName = e.target.value;
                  const proj = projectList.find(p => p.projectName === projName);
                  if (proj) {
                    setSelectedRow((prev) =>
                      prev ? {
                        ...prev,
                        projectName: proj.projectName,
                        projectId: proj.projectId,
                      } : null
                    );
                  }
                }}
              >
                {projectList.map((proj) => (
                  <MenuItem key={proj.projectId} value={proj.projectName}>
                    {proj.projectName}
                  </MenuItem>
                ))}
              </TextField>

              {/* Type */}
              <TextField
                select
                label="Type"
                fullWidth
                margin="normal"
                value={selectedRow?.type || ""}
                onChange={(e) =>
                  setSelectedRow((prev) =>
                    prev ? { ...prev, type: e.target.value } : null
                  )
                }
              >
                {["Full-time", "Part-time", "Consultant"].map((option) => (
                  <MenuItem key={option} value={selectedRow?.type}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>


              {/* Rate */}
              <TextField
                label="Rate ($)"
                fullWidth
                margin="normal"
                value={selectedRow?.rate || ""}
                onChange={(e) =>
                  setSelectedRow((prev) =>
                    prev ? { ...prev, rate: parseFloat(e.target.value) } : null
                  )
                }
              />

              {/* Utilization */}
              <TextField
                label="Utilization (%)"
                fullWidth
                margin="normal"
                value={selectedRow?.utilization || ""}
                onChange={(e) =>
                  setSelectedRow((prev) =>
                    prev ? { ...prev, utilization: parseFloat(e.target.value) } : null
                  )
                }
              />

              {/* Start Date */}
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={selectedRow?.startDate || ""}
                onChange={(e) =>
                  setSelectedRow((prev) =>
                    prev ? { ...prev, startDate: e.target.value } : null
                  )
                }
              />

              {/* End Date */}
              <TextField
                label="End Date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={selectedRow?.endDate || ""}
                onChange={(e) =>
                  setSelectedRow((prev) =>
                    prev ? { ...prev, endDate: e.target.value } : null
                  )
                }
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={confirmEdit} variant="contained">Update</Button>
              <Button onClick={() => setEditDialogOpen(false)} variant="outlined">Back</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <b>{selectedRow?.employeeName}</b>'s mapping?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirmDelete} color="error">Delete</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>

        </>
      )}
    </Box>
  );
};

export default EmpMapping;
